import type { CampaignType } from './campaign';
import type { SelectByType, WithExtraFields } from './utils';

export interface FetchPromoData {
  promotions: Array<Promotion>;
  winNotifications: Array<WinNotificationType>;
}

export interface FetchPromoConsentData {
  promotions: Array<Promotion>;
}

export interface FetchLeaderboardData {
  leaderboard: TournamentLeaderboardDetails;
  tournamentId: number;
}

export interface FetchLeaderboardAroundMeData {
  leaderboardAroundMe: TournamentLeaderboardAroundMeDetails;
  tournamentId: number;
}

export interface FetchPrizesAndWinnersData {
  prizes: PrizedropPrizeType[];
  winners: PrizeDropWinnersType[];
  prizeDropId: number;
  screenNameHidden: boolean;
}

export interface FetchCashbackScoreData {
  score: CashbackScoreType | undefined;
  cahbackId: number;
}

export interface Promotion<T extends PromotionType = PromotionType> {
  campaignType: CampaignType.DefaultPromo;
  type: T;
  id: number;
  platformId: number;
  name: string;
  brandName: string;
  opted: PromotionOptedStatus;
  gameIds: Array<string>;
  consentRequired: boolean;
  roundsType: PromoRoundType | null;
  activeDuration: PromotionDuration;
  isAsian: boolean;
  campaignDetails: {
    id: number | null;
    endDate: number | null;
  };
  ruleDetails: RuleDetails;
  prizeDetails: SelectByType<
    T,
    {
      [PromotionType.Tournament]: TournamentPrizeDetails;
      [PromotionType.Cashback]: CashbackPrizeDetails;
      [PromotionType.Prizedrop]: PrizedropPrizeDetails;
    }
  >;
  userPrizeDetails: SelectByType<
    T,
    {
      [PromotionType.Tournament]: TournamentUserPrizeDetails;
      [PromotionType.Cashback]: CashbackUserScoreDetails;
      [PromotionType.Prizedrop]: PrizeDropWinnerUserPrizeDetails;
    }
  >;
}

export interface PromotionDuration {
  fromEpoch: number;
  toEpoch: number;
}

export type TournamentPrizeList = Array<{
  fromRank: number;
  toRank: number;
  prizeDetails: PromotionPrize;
}>;
export interface TournamentPrizeDetails {
  minimumBetLimit: number;
  totalPrizeAmout: number;
  totalNoOfPrizes: number;
  highestPrize: string;
  prizes: Array<{
    fromRank: number;
    toRank: number;
    prizeDetails: PromotionPrize;
  }>;
  winCriterion: TournamentPromoWinCriterionType;
}

export interface CashbackPrizeDetails {
  minimumBetLimit: number;
  minCashbackAmount: number;
  maxCashbackAmount: number;
  type: CashbackPromoType;
}

export interface PrizedropPrizeDetails {
  minimumBetLimit: number;
  totalPrizeAmout: number;
  totalNoOfPrizes: number;
  highestPrize: string;
  prizes: PrizedropPrizeType[];
}

export interface TournamentUserPrizeDetails {
  leaderboard: TournamentLeaderboardDetails;
  leaderboard_aroundMe: TournamentLeaderboardAroundMeDetails;
}
export interface PrizeDropWinnerUserPrizeDetails {
  winners: PrizeDropWinnersType[];
  screenNameHidden: boolean;
}

export interface TournamentLeaderboardDetails {
  data: LeaderboardDataType[];
  screenNameHidden: boolean;
}

export interface TournamentLeaderboardAroundMeDetails {
  playerId: number;
  leaderboardItems: LeaderboardDataType[];
}

export interface CashbackUserScoreDetails {
  totalScore: number;
  cashbackScore: number;
  cashbackPercentage: number;
  roundLimitExceeded: boolean;
  displayScore: boolean;
}

export type PromotionPrize =
  | { type: PromotionPrizeType.Gift; gift: string }
  | { type: PromotionPrizeType.Coins; amount: number }
  | { type: PromotionPrizeType.BetMultiplier; multiplier: number }
  | { type: PromotionPrizeType.FreeRounds; freeRoundType: string }
  | { type: PromotionPrizeType.Invalid };

export type LeaderboardDataType = {
  playerId: number;
  position: number;
  scoreDate: number;
  score: number;
  screenName: string;
};

export interface PrizedropPrizeType {
  id: number;
  size: number;
  originalSize: number;
  prizeDetails: PromotionPrize;
}

export type PrizeDropWinnersType = {
  amount: number | null;
  casinoUserId: string;
  gift: string | null;
  prizeId: number;
  screenName: string;
  winDate: number;
};

export interface CashbackScoreType {
  totalScore: number;
  cashbackScore: number;
  cashbackPercentage: number;
  roundLimitExceeded: boolean;
}

export interface RoundHistoryType {
  id: number;
  gameId: string;
  tableId: string;
  dateTime: number;
  bet: number;
  win: number;
}

export interface RoundHistoryByWSType {
  streakId: number;
  winstreakCount: number;
  winstreakPoints: number;
  lastPointsWinType: LastPointsWinType;
  rounds: RoundHistoryType[];
}

export interface WinNotificationType {
  promoType: PromotionType;
  promoId: number;
  name: string;
  brandName: string;
  isAsia: boolean;
  winType: PromotionPrizeType;
  prize: string;
  endDate: number;
  ruleDetails: RuleDetails;
  autoPay: boolean;
  minBetLimit: number;
  extraData: {
    autoPay?: boolean;
    type?: CashbackPromoType;
    multiplier?: number;
    bet?: number;
    minCashback?: number;
    maxCashback?: number;
    totalNoOfPrizes?: number;
    totalPrizeAmount?: number;
    highestPrize?: string;
    prizes?: PrizedropPrizeDetails['prizes'] | TournamentPrizeList;
  } | null;
}

//Socket response
export interface PrizeDropWinSocketResponse {
  amount: string;
  autoPay: boolean;
  bet: string;
  id: string;
  mul: string;
  pdid: string;
  prizeType: PromotionPrizeType;
  seq: number;
  table: string;
  textPrize: string;
  transId: string;
  userId: string;
  wiid: string;
}

export interface DefaultDynamicFields {
  tournamentName: string;
  minBet: string | number;
  maxBet?: string | number;
  highestPrize?: string;
  ddwName?: string;
  totalPrizeAmount?: string | number;
  totalPrizeAmountBeautified?: string | number;
  highestPrizeBeautified?: string | number;
}

export interface HowToPlaySection {
  title: string;
  val: string;
}
export interface RuleDetails {
  howToPlay?: HowToPlaySection[];
  rule: string;
  lang: string;
  currency: string;
  dynamic: WithExtraFields<DefaultDynamicFields>;
  ruleShort?: string;
}

//
// Enum definitions
//
export enum PromotionType {
  Tournament = 'TOURNAMENT',
  Cashback = 'CASHBACK',
  Prizedrop = 'PRIZEDROP',
}

export enum PromotionOptedStatus {
  Opted = 'opted',
  NonOpted = 'non-opted',
  Pop = 'pop',
  NoPop = 'no_pop',
  NA = 'NA',
}


export enum PromotionPrizeType {
  Gift = 'G',
  Coins = 'C',
  BetMultiplier = 'BM',
  FreeRounds = 'FR',
  Invalid = 'Invalid',
}

export enum TournamentPromoWinCriterionType {
  GenericPoints = 'GP',
  TotalPoints = 'TP',
  TotalBet = 'TB',
  WinStreak = 'WS',
}

export enum CashbackPromoType {
  Cashback = 'CASHBACK',
  Cashboost = 'CASHBOOST',
}

export enum AsianRegions {
  Asia = 'Asia',
  Manila = 'Manila',
}

export enum DDWBrandName {
  DailyWins = 'Daily Wins',
  DropsAndWins = 'Drops & Wins',
}

export enum LastPointsWinType {
  CONSECUTIVE_LOSS = 'CONSECUTIVE_LOSS',
  CONSECUTIVE_WINS = 'CONSECUTIVE_WINS',
  CONSECUTIVE_WINS_MULTIPLIER = 'CONSECUTIVE_WINS_MULTIPLIER',
}

export enum PromoRoundType {
  Daily = 'DAILY',
  Total = 'TOTAL',
}
